/**
 * Desktop product-page layout. Two independent pieces:
 *
 *   1. The gallery rail (>= 1280px, where the gallery splits into a sticky main
 *      image plus the column of remaining media beside it).
 *   2. The info column's stick-to-the-bottom offset (>= 1024px).
 *
 * ---------------------------------------------------------------------------
 * 1. Gallery rail (>= 1280px).
 *
 * Before: that column rendered every image at full column width, so an 18-image
 * product made the gallery ~5,900px tall. The main image is sticky, so the page
 * effectively refused to move until the shopper had scrolled past every last
 * photo.
 *
 * After: the column is capped at the height of the main image and its tiles are
 * sized so four fit inside that height. Overflow is hidden rather than
 * scrollable on purpose — the wheel has to keep scrolling the *page*, which is
 * the whole point of the change. The remaining images are reached with the
 * chevron buttons this script adds.
 *
 * Tile height follows tile width (theme.css puts `aspect-ratio: var(--aspect-ratio)`
 * on the image), so sizing the rail means solving for the column width:
 *
 *     width = aspect * (railHeight - (TILES_IN_VIEW - 1) * gap) / TILES_IN_VIEW
 *
 * Everything is handed to CSS as custom properties; if this script never runs,
 * the stylesheet's `.55fr` / `height: auto` fallbacks leave the old layout in
 * place.
 *
 * ---------------------------------------------------------------------------
 * 2. Info column sticky offset (>= 1024px).
 *
 * The column should scroll normally, then pin once its last tab has reached the
 * bottom of the viewport, and release at the end of the gallery.
 *
 * `position: sticky; bottom: 24px` is the obvious way to write that, and it
 * does not work: Chrome does not apply a bottom inset to a start-aligned grid
 * item, no matter how much room the grid area has (reproduced in isolation, on
 * a column both taller and shorter than the viewport). The offset that does
 * work is the equivalent one on `top`:
 *
 *     top = 100vh - (column height) - gap
 *
 * which pins the box at exactly the same moment — when its bottom edge reaches
 * `100vh - gap` — and, being a `top` inset, is honoured. It goes negative
 * whenever the column is taller than the viewport, which is fine and expected.
 *
 * The height has to come from JS because it changes with the product and every
 * time a shopper opens an accordion, hence the ResizeObserver. Without JS the
 * custom property is unset, `top` falls back to `auto`, and the column simply
 * scrolls — the behaviour before this was added.
 */
(function () {
  'use strict';

  var TILES_IN_VIEW = 4;
  var MIN_RAIL_WIDTH = 140;
  var MAX_RAIL_FRACTION = 0.42; // of the gallery's width
  var MQ = window.matchMedia('(min-width: 1280px)');

  var SVG_NS = 'http://www.w3.org/2000/svg';

  function chevron(direction) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('d', direction === 'up' ? 'M6 15L12 9L18 15' : 'M6 9L12 15L18 9');
    svg.appendChild(path);
    return svg;
  }

  function Rail(gallery) {
    this.gallery = gallery;
    this.preview = gallery.querySelector('.product__preview');
    this.container = gallery.querySelector('.product__media-container');
    this.list = gallery.querySelector('.product__media-list');
    this.scroller = this.container && this.container.firstElementChild;
    this.buttons = null;
    this.measure = this.measure.bind(this);
    this.syncButtons = this.syncButtons.bind(this);
  }

  Rail.prototype.isUsable = function () {
    return !!(this.preview && this.container && this.list && this.scroller);
  };

  /** Tiles the shopper can actually see — variant-only media is display:none. */
  Rail.prototype.visibleTiles = function () {
    return Array.prototype.filter.call(this.list.children, function (el) {
      return el.offsetHeight > 0;
    });
  };

  Rail.prototype.measure = function () {
    if (!MQ.matches) return this.teardown();

    var tiles = this.visibleTiles();
    var first = tiles[0];
    if (!first) return;

    // Aspect comes off a rendered tile rather than --aspect-ratio, so a product
    // whose media doesn't match the section variable still lands on whole tiles.
    var aspect = first.offsetWidth / first.offsetHeight;
    if (!isFinite(aspect) || aspect <= 0) aspect = 1;

    var previewHeight = this.preview.getBoundingClientRect().height;
    if (!previewHeight) return;

    var gap = parseFloat(getComputedStyle(this.list).rowGap) || 20;
    var width = aspect * (previewHeight - (TILES_IN_VIEW - 1) * gap) / TILES_IN_VIEW;
    width = Math.max(MIN_RAIL_WIDTH, Math.min(width, this.gallery.getBoundingClientRect().width * MAX_RAIL_FRACTION));

    // A product with only two or three images shouldn't leave dead space under
    // the rail, so the rail never grows past the media it actually holds.
    var contentHeight = tiles.length * (width / aspect) + (tiles.length - 1) * gap;
    var railHeight = Math.min(previewHeight, contentHeight);

    this.gallery.style.setProperty('--pdp-rail-w', width + 'px');
    this.gallery.style.setProperty('--pdp-rail-h', railHeight + 'px');
    this.step = width / aspect + gap;

    this.ensureButtons();
    this.syncButtons();
  };

  Rail.prototype.teardown = function () {
    this.gallery.style.removeProperty('--pdp-rail-w');
    this.gallery.style.removeProperty('--pdp-rail-h');
    if (this.buttons) this.buttons.wrap.hidden = true;
  };

  Rail.prototype.ensureButtons = function () {
    if (this.buttons) {
      this.buttons.wrap.hidden = false;
      return;
    }

    var self = this;
    var wrap = document.createElement('div');
    wrap.className = 'pdp-rail__controls';

    function makeButton(direction, label) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-rail__button pdp-rail__button--' + direction;
      btn.setAttribute('aria-label', label);
      btn.appendChild(chevron(direction));
      btn.addEventListener('click', function () {
        self.page(direction === 'up' ? -1 : 1);
      });
      return btn;
    }

    var up = makeButton('up', 'Previous product images');
    var down = makeButton('down', 'More product images');
    wrap.appendChild(up);
    wrap.appendChild(down);
    this.container.appendChild(wrap);
    this.buttons = { wrap: wrap, up: up, down: down };

    // `overflow: hidden` still scrolls programmatically, and it still fires
    // scroll events when a focused tile is scrolled into view by the browser.
    this.scroller.addEventListener('scroll', this.syncButtons, { passive: true });
  };

  /** Advance by a whole screenful of tiles, landing on a tile boundary. */
  Rail.prototype.page = function (direction) {
    var step = this.step || this.scroller.clientHeight;
    var perPage = Math.max(1, Math.round(this.scroller.clientHeight / step));
    var index = Math.round(this.scroller.scrollTop / step) + direction * perPage;
    var max = this.scroller.scrollHeight - this.scroller.clientHeight;
    this.scroller.scrollTo({ top: Math.max(0, Math.min(index * step, max)), behavior: 'smooth' });
  };

  Rail.prototype.syncButtons = function () {
    if (!this.buttons) return;
    var el = this.scroller;
    var max = el.scrollHeight - el.clientHeight;
    // Sub-pixel rounding leaves a stray pixel or two at either end.
    this.buttons.up.hidden = el.scrollTop <= 2;
    this.buttons.down.hidden = el.scrollTop >= max - 2;
    this.buttons.wrap.hidden = max <= 2;
  };

  var MQ_STICKY = window.matchMedia('(min-width: 1024px)');

  function stickyInfo(info) {
    function apply() {
      if (!MQ_STICKY.matches) {
        info.style.removeProperty('--pdp-info-sticky-top');
        return;
      }
      var height = Math.ceil(info.getBoundingClientRect().height);
      info.style.setProperty('--pdp-info-sticky-top', 'calc(100vh - ' + height + 'px - var(--sp-6))');
    }

    apply();

    // Opening a tab changes the column's height, which changes where it should
    // pin. Watching the box itself covers that, variant swaps, and app widgets
    // that render late.
    if (window.ResizeObserver) new ResizeObserver(apply).observe(info);
    window.addEventListener('resize', apply);
    if (MQ_STICKY.addEventListener) MQ_STICKY.addEventListener('change', apply);
    else MQ_STICKY.addListener(apply);
  }

  function init() {
    var infos = document.querySelectorAll('product-info.product__info');
    Array.prototype.forEach.call(infos, function (info) {
      if (info.dataset.pdpSticky) return;
      info.dataset.pdpSticky = 'true';
      stickyInfo(info);
    });

    var galleries = document.querySelectorAll('.product--scroll media-gallery.product__gallery');
    Array.prototype.forEach.call(galleries, function (gallery) {
      if (gallery.dataset.pdpRail) return;
      var rail = new Rail(gallery);
      if (!rail.isUsable()) return;
      gallery.dataset.pdpRail = 'true';

      rail.measure();

      // The main image is sized off the viewport and the header's scrolled
      // state, so its height changes without a window resize.
      if (window.ResizeObserver) {
        new ResizeObserver(rail.measure).observe(rail.preview);
      }
      window.addEventListener('resize', rail.measure);
      if (MQ.addEventListener) MQ.addEventListener('change', rail.measure);
      else MQ.addListener(rail.measure);

      // Switching variants can show or hide variant-attached media.
      new MutationObserver(rail.measure).observe(rail.list, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'style', 'class'],
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Section re-render in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
